import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { useBlog } from "../useHook";
import "react-quill/dist/quill.snow.css";
import Swal from "sweetalert2";
import Loader from "../../../Utils/loader";
import {
  usePermission,
  SystemPermissions,
} from "../../../Utils/permissionAccess";
import { Apis } from "../../../lib/Apis";
import { Calculator, ChevronDown } from "lucide-react";

const normalizeImageUrl = (url) => {
  if (!url || typeof url !== "string") return url;
  let normalized = url.replace(/\/api(?=\/)/, "");
  if (
    typeof window !== "undefined" &&
    window.location?.protocol === "https:" &&
    normalized.startsWith("http://")
  ) {
    normalized = "https:" + normalized.slice(5);
  }
  try {
    return encodeURI(decodeURI(normalized));
  } catch {
    return normalized;
  }
};

const BlogView = () => {
  const { id } = useParams();
  const { getBlogById, addOnHeaderBlog, addPopularBlog } = useBlog();
  const [blog, setBlog] = useState(null);
  const [loading, setLoading] = useState(true);
  const [costData, setCostData] = useState(null);
  const [loadingCost, setLoadingCost] = useState(false);

  const { hasPermission } = usePermission();
  const canPopular = hasPermission(SystemPermissions.Blogs.ADD_TO_MOST_POPULAR);
  const canFeatured = hasPermission(SystemPermissions.Blogs.ADD_FEATURED_BLOG);
  const [showPopularModal, setShowPopularModal] = useState(false);
  const [showHeaderModal, setShowHeaderModal] = useState(false);

  const fetchCostTables = async (marla, mode) => {
    try {
      setLoadingCost(true);
      const res = await Apis.getCostConstructionTables(marla, mode);
      const data = res?.data?.data || res?.data;
      setCostData(data);
    } catch (err) {
      console.error("Error fetching cost tables for blog:", err);
    } finally {
      setLoadingCost(false);
    }
  };

  const fetchBlog = async () => {
    try {
      const blogData = await getBlogById(id);
      setBlog(blogData);
      if (blogData?.cost_marla) {
        fetchCostTables(blogData.cost_marla, blogData.cost_mode || "marla");
      }
    } catch (error) {
      console.error("Error fetching blog:", error);
      Swal.fire({
        title: "Error",
        text: "Failed to fetch blog details",
        icon: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBlog();
  }, [id]);

  const handlePopularToggle = async () => {
    try {
      const response = await addPopularBlog({ blogId: parseInt(id) });
      if (response) {
        setBlog((prev) => ({ ...prev, isPopular: !prev.isPopular }));
        setShowPopularModal(false);
        Swal.fire({
          title: "Success",
          text: blog.isPopular
            ? "Blog removed from popular"
            : "Blog added to popular",
          icon: "success",
          timer: 2000,
          showConfirmButton: false,
        });
      }
    } catch (error) {
      console.error("Error toggling popular status:", error);
      Swal.fire({
        title: "Error",
        text: "Error toggling popular status",
        icon: "error",
      });
    }
  };

  const handleHeaderToggle = async () => {
    try {
      const response = await addOnHeaderBlog({ blogId: parseInt(id) });
      if (response) {
        setBlog((prev) => ({ ...prev, isHeader: !prev.isHeader }));
        setShowHeaderModal(false);
        Swal.fire({
          title: "Success",
          text: blog.isHeader
            ? "Blog removed from header"
            : "Blog added to header",
          icon: "success",
          timer: 2000,
          showConfirmButton: false,
        });
      }
    } catch (error) {
      console.error("Error toggling header status:", error);
      Swal.fire({
        title: "Error",
        text: "Error toggling header status",
        icon: "error",
      });
    }
  };

  if (loading) {
    return <Loader message="Loading blog details..." />;
  }

  if (!blog) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-800 mb-4">
            Blog not found
          </h2>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="bg-gray-100 p-4 flex flex-wrap justify-between items-center gap-3 mb-6">
        <h2 className="text-2xl font-bold">Blog Details</h2>
        <div className="flex flex-wrap gap-3">
          {canPopular && (
            <button
              type="button"
              onClick={() => setShowPopularModal(true)}
              className={`btn justify-center ${
                blog.isPopular ? "btn-delete text-white" : "btn-primary"
              }`}
            >
              {blog.isPopular ? "Remove from Popular" : "Add to Most Popular"}
            </button>
          )}
          {canFeatured && (
            <button
              type="button"
              onClick={() => setShowHeaderModal(true)}
              className={`btn justify-center ${
                blog.isHeader ? "btn-delete text-white" : "btn-primary"
              }`}
            >
              {blog.isHeader
                ? "Remove from Featured Blog"
                : "Add Featured Blog"}
            </button>
          )}
        </div>
      </div>

      <div className="container mx-auto px-4 pb-8">
        <div className="bg-white rounded-lg shadow-md p-6 mb-8">
          <div className="flex items-center gap-4 mb-6">
            <span
              className={`px-4 py-2 rounded-full text-sm font-medium ${
                blog.status === "published"
                  ? "bg-green-100 text-green-800"
                  : "bg-yellow-100 text-yellow-800"
              }`}
            >
              {blog.status === "published" ? "Published" : "Draft"}
            </span>
            <span className="px-4 py-2 bg-gray-100 text-gray-800 rounded-full text-sm font-medium">
              Category: {blog.category?.name || "Uncategorized"}
            </span>
            {blog.cost_marla && (
              <span className="px-4 py-2 bg-purple-100 text-[#982598] rounded-full text-sm font-bold flex items-center gap-1.5 border border-purple-200">
                <Calculator size={14} />
                Linked Cost Table:{" "}
                {blog.cost_mode === "sqYards"
                  ? `${blog.cost_marla * 25} Sq Yards`
                  : `${blog.cost_marla} Marla`}{" "}
                ({blog.cost_mode === "sqYards" ? "Sq Yards Mode" : "Marla Mode"})
              </span>
            )}
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-md overflow-hidden">
          {blog.headerImage && (
            <div className="w-full relative min-h-[280px] bg-gray-100">
              <img
                src={normalizeImageUrl(blog.headerImage)}
                alt={blog.name}
                className="w-full h-full min-h-[280px] object-cover object-center"
              />
            </div>
          )}

          <div className="p-8">
            <div className="mb-8">
              <h1 className="text-4xl font-bold text-gray-900 mb-4">
                {blog.name}
              </h1>
              <p className="text-xl text-gray-600 leading-relaxed">
                {blog.description}
              </p>
            </div>

            <div className="prose max-w-none">
              {blog.content?.map((block, index) => (
                <div key={index} className="mb-8">
                  {block.type === "text" ? (
                    <div
                      dangerouslySetInnerHTML={{ __html: block.content }}
                      className="blog-content text-gray-700 leading-relaxed"
                    />
                  ) : (
                    <div className="flex justify-center">
                      <img
                        src={normalizeImageUrl(block.content)}
                        alt={`Blog content ${index + 1}`}
                        className="max-w-full rounded-lg shadow-lg"
                        onError={(e) => {
                          e.target.style.display = "none";
                        }}
                      />
                    </div>
                  )}
                </div>
              ))}
            </div>

            {/* Dynamic Cost Tables Section if linked */}
            {blog.cost_marla && (
              <div className="mt-12 pt-8 border-t border-gray-200">
                <div className="rounded-2xl bg-gradient-to-r from-[#15173d] to-[#2d1050] p-6 text-white mb-6 shadow-lg">
                  <div className="flex flex-wrap items-center justify-between gap-4">
                    <div>
                      <p className="text-[11px] font-bold uppercase tracking-wider text-purple-200 mb-1">
                        Dynamic Cost Table Integration
                      </p>
                      <h2 className="text-2xl font-black">
                        Cost of Construction — {costData?.displayStr || `${blog.cost_marla} Marla`}
                      </h2>
                      <p className="text-xs text-purple-200/70 mt-1">
                        18 live-calculated tables based on current market rates and floor covered areas
                      </p>
                    </div>
                    <div className="text-right">
                      <span className="inline-block px-3 py-1 rounded-full bg-white/10 text-xs font-bold text-white border border-white/20">
                        {blog.cost_mode === "sqYards" ? "📐 Sq Yards Mode" : "🏠 Marla Mode"}
                      </span>
                    </div>
                  </div>
                </div>

                {loadingCost ? (
                  <div className="py-12 text-center text-sm font-bold text-gray-500">
                    Loading 18 cost tables...
                  </div>
                ) : costData?.tables?.length > 0 ? (
                  <div className="space-y-6">
                    {[1, 2, 3, 4, 5, 6].map((htId) => {
                      const groupTables = costData.tables.filter((t) => t.htId === htId);
                      const htLabels = {
                        1: "Single Storey House with Mumty",
                        2: "Double Storey House — Ground + First + Mumty",
                        3: "Double Storey House — Basement + Ground + Mumty",
                        4: "Triple Storey House — Ground + First + Second + Mumty",
                        5: "Triple Storey House — Basement + Ground + First + Mumty",
                        6: "Four Storey House — Basement + Ground + First + Second + Mumty",
                      };

                      return (
                        <div key={htId} className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
                          <div className="flex items-center gap-3 mb-4 pb-3 border-b border-gray-100">
                            <span className="flex h-7 w-7 items-center justify-center rounded-lg bg-[#982598] text-xs font-black text-white">
                              {htId}
                            </span>
                            <h3 className="text-base font-extrabold text-[#15173d]">
                              {htLabels[htId]}
                            </h3>
                          </div>

                          <div className="space-y-3">
                            {groupTables.map((tbl) => {
                              const qualityStyles = {
                                budget: { label: "Budget", badgeBg: "#dbeafe", badgeColor: "#1e40af", headBg: "#eff6ff" },
                                aQuality: { label: "A Quality", badgeBg: "#dcfce7", badgeColor: "#14532d", headBg: "#f0fdf4" },
                                premium: { label: "Premium", badgeBg: "#fef3c7", badgeColor: "#92400e", headBg: "#fffbeb" },
                              };
                              const qs = qualityStyles[tbl.quality] || qualityStyles.budget;
                              const fmtVal = (v) => (v ? "Rs " + Math.round(v).toLocaleString("en-PK") : "—");
                              const fmtNum = (v) => (v ? Math.round(v).toLocaleString("en-PK") : "0");

                              return (
                                <details
                                  key={tbl.id}
                                  className="group rounded-lg border border-gray-200 bg-white overflow-hidden"
                                  open={tbl.quality === "budget"}
                                >
                                  <summary className="flex cursor-pointer items-center justify-between px-4 py-3 bg-gray-50/50 hover:bg-gray-100/70 transition-colors select-none">
                                    <div className="flex items-center gap-2 flex-wrap">
                                      <span
                                        style={{ backgroundColor: qs.badgeBg, color: qs.badgeColor }}
                                        className="rounded px-2 py-0.5 text-[10px] font-black"
                                      >
                                        {qs.label}
                                      </span>
                                      <span className="text-xs font-bold text-gray-800">
                                        {tbl.title}
                                      </span>
                                    </div>
                                    <div className="flex items-center gap-3">
                                      {tbl.grandTotal > 0 && (
                                        <span className="text-xs font-black text-[#982598]">
                                          {fmtVal(tbl.grandTotal)}
                                        </span>
                                      )}
                                      <ChevronDown size={14} className="text-gray-400 group-open:rotate-180 transition-transform" />
                                    </div>
                                  </summary>

                                  <div className="border-t border-gray-200">
                                    <div className="px-4 py-2" style={{ backgroundColor: qs.headBg }}>
                                      <p className="text-[11px] text-gray-600 italic">{tbl.subTitle}</p>
                                    </div>
                                    <div className="overflow-x-auto">
                                      <table className="w-full text-left text-xs border-collapse">
                                        <thead>
                                          <tr className="bg-gray-50 text-gray-500 font-bold border-b border-gray-200 text-[11px]">
                                            <th className="py-2 px-3">Floor</th>
                                            <th className="py-2 px-3 text-right">Area (Sq Ft)</th>
                                            <th className="py-2 px-3 text-right">Grey Structure</th>
                                            <th className="py-2 px-3 text-right">Finishing</th>
                                            <th className="py-2 px-3 text-right">Total</th>
                                          </tr>
                                        </thead>
                                        <tbody className="divide-y divide-gray-100 text-[11px]">
                                          {tbl.rows?.map((r, ri) => (
                                            <tr key={ri} className={ri % 2 === 0 ? "bg-white" : "bg-gray-50/40"}>
                                              <td className="py-2 px-3 font-semibold text-gray-700">{r.floor}</td>
                                              <td className="py-2 px-3 text-right text-gray-600">{fmtNum(r.area)}</td>
                                              <td className="py-2 px-3 text-right text-gray-700">{fmtVal(r.grey)}</td>
                                              <td className="py-2 px-3 text-right text-gray-700">{fmtVal(r.finish)}</td>
                                              <td className="py-2 px-3 text-right font-bold text-gray-900">{fmtVal(r.total)}</td>
                                            </tr>
                                          ))}
                                          <tr className="bg-gradient-to-r from-[#15173d] to-[#2d1050] text-white font-extrabold text-xs">
                                            <td className="py-2.5 px-3">TOTAL</td>
                                            <td className="py-2.5 px-3 text-right text-white/80">{fmtNum(tbl.totArea)}</td>
                                            <td className="py-2.5 px-3 text-right text-purple-200">{fmtVal(tbl.totGrey)}</td>
                                            <td className="py-2.5 px-3 text-right text-purple-200">{fmtVal(tbl.totFinish)}</td>
                                            <td className="py-2.5 px-3 text-right text-[#f0c6ff] font-black">{fmtVal(tbl.grandTotal)}</td>
                                          </tr>
                                        </tbody>
                                      </table>
                                    </div>
                                    {tbl.summaryText && (
                                      <div className="px-4 py-2.5 bg-[#faf9ff] border-t border-gray-100">
                                        <p className="text-[11px] text-gray-700 italic">{tbl.summaryText}</p>
                                      </div>
                                    )}
                                  </div>
                                </details>
                              );
                            })}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <p className="text-xs text-gray-400 py-4 text-center">
                    No cost data available for this marla size yet.
                  </p>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {showPopularModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-lg shadow-xl max-w-md w-full">
            <h3 className="text-xl font-bold mb-4">Confirm Action</h3>
            <p className="mb-6 text-gray-600">
              {blog.isPopular
                ? "Are you sure you want to remove this blog from popular?"
                : "Are you sure you want to add this blog to popular?"}
            </p>
            <div className="flex justify-end gap-3">
              <button
                type="button"
                onClick={() => setShowPopularModal(false)}
                className="btn btn-reset-filter justify-center"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handlePopularToggle}
                className={`btn justify-center ${
                  blog.isPopular ? "btn-delete text-white" : "btn-primary"
                }`}
              >
                {blog.isPopular ? "Remove" : "Save"}
              </button>
            </div>
          </div>
        </div>
      )}

      {showHeaderModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-lg shadow-xl max-w-md w-full">
            <h3 className="text-xl font-bold mb-4">Confirm Action</h3>
            <p className="mb-6 text-gray-600">
              {blog.isHeader
                ? "Are you sure you want to remove this blog from featured blog?"
                : "Are you sure you want to show this blog on featured blog?"}
            </p>
            <div className="flex justify-end gap-3">
              <button
                type="button"
                onClick={() => setShowHeaderModal(false)}
                className="btn btn-reset-filter justify-center"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleHeaderToggle}
                className={`btn justify-center ${
                  blog.isHeader ? "btn-delete text-white" : "btn-primary"
                }`}
              >
                {blog.isHeader ? "Remove" : "Save"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BlogView;
